import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import type { EventClickArg, EventMountArg, EventContentArg, DatesSetArg } from '@fullcalendar/core';

const TOOLTIP_DELAY_MS = 150;
const TOOLTIP_CURSOR_OFFSET_X = 12;
const TOOLTIP_CURSOR_OFFSET_Y = -8;

interface Campaign {
  id: string;
  nome: string;
  status: string;
  scheduledFor: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function escapeHtml(text: string) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  allDay?: boolean;
  className?: string;
  extendedProps: { campaignId: string; type: 'scheduled' | 'sent'; timeLabel: string; sortKey: number };
}

type TooltipState = { text: string; x: number; y: number } | null;

export function CampaignSchedulesPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState>(null);
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipMouseRef = useRef({ x: 0, y: 0 });
  const tooltipListenersRef = useRef<{ el: HTMLElement | null; handler: ((e: MouseEvent) => void) | null }>({ el: null, handler: null });
  const calendarRef = useRef<FullCalendar | null>(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return fetch(url, { ...options, headers });
  };

  const loadCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch('/api/campaigns?limit=1000');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setCampaigns(data.campaigns ?? []);
    } catch (err) {
      console.error('Erro ao carregar campanhas:', err);
      setError('Erro ao carregar agendamentos. Tente novamente.');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const events: CalendarEvent[] = useMemo(() => {
    const list: CalendarEvent[] = [];
    for (const c of campaigns) {
      if (c.scheduledFor && (c.status === 'PENDING' || c.status === 'RUNNING')) {
        const timeStr = formatTime(c.scheduledFor);
        list.push({
          id: `sched-${c.id}`,
          title: c.nome,
          start: c.scheduledFor,
          allDay: true,
          className: 'fc-event-agendada',
          extendedProps: {
            campaignId: c.id,
            type: 'scheduled',
            timeLabel: `Agendada para ${timeStr}`,
            sortKey: new Date(c.scheduledFor).getTime(),
          },
        });
      }
      if (c.completedAt && c.status === 'COMPLETED') {
        const dayStart = new Date(c.completedAt);
        dayStart.setHours(0, 0, 0, 0);
        const timeStr = formatTime(c.completedAt);
        list.push({
          id: `sent-${c.id}`,
          title: c.nome,
          start: dayStart.toISOString(),
          allDay: true,
          className: 'fc-event-enviada',
          extendedProps: {
            campaignId: c.id,
            type: 'sent',
            timeLabel: `Enviada às ${timeStr}`,
            sortKey: new Date(c.completedAt).getTime(),
          },
        });
      }
    }
    list.sort((a, b) => a.extendedProps.sortKey - b.extendedProps.sortKey);
    return list;
  }, [campaigns]);

  const handleEventDidMount = (arg: EventMountArg) => {
    const label = (arg.event.extendedProps as { timeLabel?: string }).timeLabel;
    if (!label || !arg.el) return;
    const el = arg.el as HTMLElement;

    const onMouseMove = (e: MouseEvent) => {
      tooltipMouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const show = () => {
      const handler = onMouseMove;
      tooltipListenersRef.current = { el, handler };
      el.addEventListener('mousemove', handler);
      tooltipTimeoutRef.current = setTimeout(() => {
        let { x, y } = tooltipMouseRef.current;
        if (x === 0 && y === 0) {
          const rect = el.getBoundingClientRect();
          x = rect.left + rect.width / 2;
          y = rect.top;
        }
        setTooltip({
          text: label,
          x: x + TOOLTIP_CURSOR_OFFSET_X,
          y: y + TOOLTIP_CURSOR_OFFSET_Y,
        });
      }, TOOLTIP_DELAY_MS);
    };

    const hide = () => {
      const { el: listenEl, handler } = tooltipListenersRef.current;
      if (listenEl && handler) listenEl.removeEventListener('mousemove', handler);
      tooltipListenersRef.current = { el: null, handler: null };
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }
      setTooltip(null);
    };

    el.addEventListener('mouseenter', show);
    el.addEventListener('mouseleave', hide);
  };

  const renderEventContent = (arg: EventContentArg) => {
    return {
      html: `<span class="fc-event-main">${escapeHtml(arg.event.title)}</span>`,
    };
  };

  const handleEventClick = (arg: EventClickArg) => {
    arg.jsEvent.preventDefault();
    const { campaignId } = arg.event.extendedProps as { campaignId: string; type: 'scheduled' | 'sent' };
    navigate(`/campanhas/relatorios/${campaignId}`);
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    const start = arg.view.currentStart;
    setCurrentMonth(new Date(start.getFullYear(), start.getMonth(), 1));
  };

  const goToPrevMonth = () => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.prev();
  };

  const goToNextMonth = () => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.next();
  };

  const goToToday = () => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.today();
  };

  const monthLabel = useMemo(
    () => currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
    [currentMonth]
  );

  if (error) {
    return (
      <>
        <Header title="Agendamentos" compactTopbar />
        <PageLayout>
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03] p-8 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Header title="Agendamentos" compactTopbar />
      <PageLayout>
        <PageHeader
          title="Agendamentos"
          breadcrumbs={[
            { label: 'Campanhas', href: '/campanhas' },
            { label: 'Agendamentos' },
          ]}
        />
        <div className="card-padrao overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Calendário</h2>
              <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/40 px-2 py-1.5 text-sm text-gray-700 dark:text-gray-200">
                <button
                  type="button"
                  onClick={goToPrevMonth}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                  aria-label="Mês anterior"
                >
                  <i className="fas fa-chevron-left text-xs" aria-hidden />
                </button>
                <span className="px-2 font-medium capitalize">{monthLabel}</span>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                  aria-label="Próximo mês"
                >
                  <i className="fas fa-chevron-right text-xs" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={goToToday}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                >
                  <i className="fas fa-calendar-day text-xs" aria-hidden />
                  Hoje
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="fc-legend-dot fc-legend-agendada" aria-hidden />
                Agendadas
              </span>
              <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="fc-legend-dot fc-legend-enviada" aria-hidden />
                Enviadas
              </span>
            </div>
          </div>
          <div className="px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-600 border-t-emerald-500" />
                <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Carregando calendário...</span>
              </div>
            ) : (
              <div className="campaign-schedules-calendar min-h-[420px]">
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={false}
                  datesSet={handleDatesSet}
                  locale={ptBrLocale}
                  events={events}
                  eventOrder={(a, b) => (a.extendedProps as { sortKey: number }).sortKey - (b.extendedProps as { sortKey: number }).sortKey}
                  eventClick={handleEventClick}
                  eventDidMount={handleEventDidMount}
                  eventContent={renderEventContent}
                  height="auto"
                  dayMaxEvents={4}
                  moreLinkClick="popover"
                  eventDisplay="block"
                  firstDay={0}
                />
              </div>
            )}
          </div>
        </div>
      </PageLayout>
      {tooltip && (
        <div
          className="fixed z-[9999] px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
          role="tooltip"
        >
          {tooltip.text}
        </div>
      )}
      <style>{`
        .campaign-schedules-calendar {
          --fc-border-color: #e2e8f0;
          --fc-page-bg-color: transparent;
          --fc-neutral-bg-color: #f8fafc;
          --fc-today-bg-color: rgba(0, 128, 112, 0.06);
          --fc-list-event-hover-bg-color: #f1f5f9;
        }
        .campaign-schedules-calendar .fc {
          font-family: inherit;
        }
        .campaign-schedules-calendar .fc-header-toolbar {
          margin-bottom: 1.25rem;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        .campaign-schedules-calendar .fc-toolbar-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
        }
        .campaign-schedules-calendar .fc-button-group {
          display: inline-flex;
          border-radius: 0.5rem;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          background: #fff;
        }
        .campaign-schedules-calendar .fc-button {
          height: 2.5rem;
          padding: 0 1rem;
          font-size: 0.875rem;
          font-weight: 500;
          border: none;
          border-radius: 0;
          background: #fff;
          color: #475569;
          text-transform: none;
          box-shadow: none;
        }
        .campaign-schedules-calendar .fc-button:not(:last-child) {
          border-right: 1px solid #e2e8f0;
        }
        .campaign-schedules-calendar .fc-button:hover {
          background: #f1f5f9;
          color: #1e293b;
        }
        .campaign-schedules-calendar .fc-button.fc-button-active {
          background: #008070;
          color: #fff;
        }
        .campaign-schedules-calendar .fc-button.fc-button-active:hover {
          background: #006658;
          color: #fff;
        }
        .campaign-schedules-calendar .fc-scrollgrid {
          border-radius: 0.75rem;
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }
        .campaign-schedules-calendar .fc-theme-standard td,
        .campaign-schedules-calendar .fc-theme-standard th {
          border-color: #e2e8f0;
        }
        .campaign-schedules-calendar .fc-col-header-cell {
          background: #f8fafc;
          padding: 0.5rem 0;
        }
        .campaign-schedules-calendar .fc-col-header-cell-cushion {
          font-size: 0.75rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }
        .campaign-schedules-calendar .fc-daygrid-day {
          background: #fff;
        }
        .campaign-schedules-calendar .fc-daygrid-day-number {
          font-size: 0.875rem;
          font-weight: 500;
          color: #64748b;
          margin: 0.25rem;
          width: 1.75rem;
          height: 1.75rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.5rem;
        }
        .campaign-schedules-calendar .fc-day-today .fc-daygrid-day-number {
          background: #008070;
          color: #fff;
        }
        .campaign-schedules-calendar .fc-daygrid-event {
          border-radius: 0.5rem;
          padding: 0.25rem 0.5rem;
          font-size: 0.8125rem;
          font-weight: 500;
          border: 1px solid transparent;
        }
        .campaign-schedules-calendar .fc-event {
          cursor: pointer;
        }
        .campaign-schedules-calendar .fc-event:hover {
          opacity: 0.92;
        }
        .campaign-schedules-calendar .fc-event-agendada {
          background: #eff6ff;
          color: #1d4ed8;
          border-color: #bfdbfe;
        }
        .campaign-schedules-calendar .fc-event-agendada .fc-event-main {
          color: inherit;
        }
        .campaign-schedules-calendar .fc-event-enviada {
          background: #ecfdf5;
          color: #047857;
          border-color: #a7f3d0;
        }
        .campaign-schedules-calendar .fc-event-enviada .fc-event-main {
          color: inherit;
        }
        .fc-legend-dot {
          width: 0.75rem;
          height: 0.75rem;
          border-radius: 9999px;
          shrink: 0;
        }
        .fc-legend-agendada {
          background: #3b82f6;
          border: 1px solid #2563eb;
        }
        .fc-legend-enviada {
          background: #10b981;
          border: 1px solid #059669;
        }
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button-group {
          border: 1px solid rgba(16, 185, 129, 0.7);
          border-radius: 0.75rem;
          overflow: hidden;
          background: rgba(236, 253, 245, 0.8);
        }
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button {
          background: transparent !important;
          color: #047857 !important;
          border: none !important;
          border-right: none !important;
          border-radius: 0;
          outline: none !important;
          box-shadow: none !important;
        }
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button:focus,
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button:focus-visible {
          outline: none !important;
          box-shadow: none !important;
          border: none !important;
          border-right: none !important;
        }
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-prev-button,
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-next-button {
          background: transparent !important;
          color: #047857 !important;
        }
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-prev-button:hover,
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-next-button:hover,
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-prev-button.fc-button-active:hover,
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-next-button.fc-button-active:hover {
          background: rgba(209, 250, 229, 0.9) !important;
          color: #047857 !important;
        }
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button:first-child {
          border-top-left-radius: 0.75rem;
          border-bottom-left-radius: 0.75rem;
        }
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button:last-child {
          border-radius: 0 0.75rem 0.75rem 0 !important;
          overflow: hidden;
        }
        .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button:last-child:hover {
          background: #006658 !important;
          color: #fff !important;
          border-radius: 0 0.75rem 0.75rem 0 !important;
        }
        .campaign-schedules-calendar .fc-daygrid-day-bottom {
          display: flex;
          justify-content: flex-end;
          text-align: right;
        }
        .campaign-schedules-calendar .fc-more-link {
          font-size: 0.8125rem;
          color: #008070;
          font-weight: 500;
        }
        .campaign-schedules-calendar .fc-popover {
          border-radius: 0.75rem;
          border: 1px solid #e2e8f0;
          box-shadow: 0 10px 25px -5px rgb(0 0 0 / 0.1);
          min-width: 240px;
          max-width: 320px;
          background: #ffffff;
        }
        .campaign-schedules-calendar .fc-popover-header {
          background: #f8fafc;
          font-weight: 600;
          font-size: 0.875rem;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e2e8f0;
        }
        .campaign-schedules-calendar .fc-popover-body {
          padding: 0.5rem 0.75rem;
          max-height: 320px;
          overflow-y: auto;
          background: #ffffff;
        }
        .campaign-schedules-calendar .fc-popover .fc-event-agendada {
          background: #eff6ff;
          border-color: #bfdbfe;
        }
        .campaign-schedules-calendar .fc-popover .fc-event-enviada {
          background: #ecfdf5;
          border-color: #a7f3d0;
        }
        .campaign-schedules-calendar .fc-popover-body-unbalanced,
        .campaign-schedules-calendar .fc-popover .fc-daygrid-body {
          display: block !important;
        }
        .campaign-schedules-calendar .fc-popover .fc-daygrid-event-harness {
          position: static !important;
          display: block !important;
          margin-bottom: 0.375rem;
        }
        .campaign-schedules-calendar .fc-popover .fc-daygrid-event-harness:last-child {
          margin-bottom: 0;
        }
        .campaign-schedules-calendar .fc-popover .fc-daygrid-event {
          position: static !important;
          margin: 0 !important;
          padding: 0.5rem 0.6rem;
          display: block !important;
          width: 100% !important;
          box-sizing: border-box;
        }
        .campaign-schedules-calendar .fc-popover .fc-event-main {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        /* Dark mode */
        .dark .campaign-schedules-calendar {
          --fc-border-color: #374151;
          --fc-page-bg-color: transparent;
          --fc-neutral-bg-color: #1f2937;
          --fc-today-bg-color: rgba(0, 168, 150, 0.12);
          --fc-list-event-hover-bg-color: #374151;
        }
        .dark .campaign-schedules-calendar .fc-toolbar-title {
          color: #f3f4f6;
        }
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:first-child .fc-button-group {
          border-color: #4b5563;
          background: #374151;
        }
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:first-child .fc-button {
          background: #374151;
          color: #d1d5db;
        }
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button-group {
          border: 1px solid rgba(16, 185, 129, 0.5);
          background: rgba(16, 185, 129, 0.1);
        }
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button {
          background: transparent !important;
          color: #34d399 !important;
          border: none !important;
          border-right: none !important;
          border-radius: 0;
          outline: none !important;
          box-shadow: none !important;
        }
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button:focus,
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button:focus-visible {
          outline: none !important;
          box-shadow: none !important;
          border: none !important;
          border-right: none !important;
        }
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-prev-button,
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-next-button {
          background: transparent !important;
          color: #34d399 !important;
        }
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-prev-button:hover,
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-next-button:hover,
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-prev-button.fc-button-active:hover,
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-next-button.fc-button-active:hover {
          background: rgba(16, 185, 129, 0.2) !important;
          color: #34d399 !important;
        }
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button:first-child {
          border-top-left-radius: 0.75rem;
          border-bottom-left-radius: 0.75rem;
        }
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button:last-child {
          border-radius: 0 0.75rem 0.75rem 0 !important;
          overflow: hidden;
        }
        .dark .campaign-schedules-calendar .fc-toolbar-chunk:last-child .fc-button:last-child:hover {
          background: #008070 !important;
          color: #fff !important;
          border-radius: 0 0.75rem 0.75rem 0 !important;
        }
        .dark .campaign-schedules-calendar .fc-button-group {
          border-color: #4b5563;
          background: #374151;
        }
        .dark .campaign-schedules-calendar .fc-button {
          background: #374151;
          color: #d1d5db;
        }
        .dark .campaign-schedules-calendar .fc-button:not(:last-child) {
          border-right-color: #4b5563;
        }
        .dark .campaign-schedules-calendar .fc-button:hover {
          background: #4b5563;
          color: #f3f4f6;
        }
        .dark .campaign-schedules-calendar .fc-button.fc-button-active {
          background: #00a896;
          color: #fff;
        }
        .dark .campaign-schedules-calendar .fc-button.fc-button-active:hover {
          background: #008070;
          color: #fff;
        }
        .dark .campaign-schedules-calendar .fc-scrollgrid {
          border-color: #374151;
        }
        .dark .campaign-schedules-calendar .fc-theme-standard td,
        .dark .campaign-schedules-calendar .fc-theme-standard th {
          border-color: #374151;
        }
        .dark .campaign-schedules-calendar .fc-col-header-cell {
          background: #1f2937;
        }
        .dark .campaign-schedules-calendar .fc-col-header-cell-cushion {
          color: #9ca3af;
        }
        .dark .campaign-schedules-calendar .fc-daygrid-day {
          background: rgba(255, 255, 255, 0.02);
        }
        .dark .campaign-schedules-calendar .fc-daygrid-day-number {
          color: #9ca3af;
        }
        .dark .campaign-schedules-calendar .fc-day-today .fc-daygrid-day-number {
          background: #00a896;
          color: #fff;
        }
        .dark .campaign-schedules-calendar .fc-event-agendada {
          background: rgba(59, 130, 246, 0.2);
          color: #93c5fd;
          border-color: rgba(59, 130, 246, 0.4);
        }
        .dark .campaign-schedules-calendar .fc-event-enviada {
          background: rgba(16, 185, 129, 0.2);
          color: #34d399;
          border-color: rgba(16, 185, 129, 0.4);
        }
        .dark .fc-legend-agendada {
          background: #3b82f6;
          border-color: #2563eb;
        }
        .dark .fc-legend-enviada {
          background: #10b981;
          border-color: #059669;
        }
        .dark .campaign-schedules-calendar .fc-more-link {
          color: #34d399;
        }
        .dark .campaign-schedules-calendar .fc-popover {
          border-color: #4b5563;
          background: #1f2937;
        }
        .dark .campaign-schedules-calendar .fc-popover-header {
          background: #374151;
          color: #f3f4f6;
          border-bottom-color: #4b5563;
        }
        .dark .campaign-schedules-calendar .fc-popover-body {
          background: #1f2937;
        }
        .dark .campaign-schedules-calendar .fc-popover .fc-event-agendada {
          background: #1e3a5f;
          border-color: #2563eb;
          color: #93c5fd;
        }
        .dark .campaign-schedules-calendar .fc-popover .fc-event-enviada {
          background: #064e3b;
          border-color: #059669;
          color: #34d399;
        }
      `}</style>
    </>
  );
}
